import { useMemo, type ReactNode } from "react";
import type { IaChatEntity, IaChatEntityKind } from "@bitacorapro/api-types";
import { parseMarkdown, type MdBlock, type MdInline } from "../lib/markdown";

// Maps the constrained markdown AST (see lib/markdown.ts) straight to
// React elements/text children — no dangerouslySetInnerHTML, no
// HTML-string intermediate anywhere. This is the security boundary for
// LLM chat answers (ADR-0050): React escapes text content by default,
// so `<img src=x onerror=…>` in an answer renders as inert text, never
// as a DOM node. No prop on any emitted element ever carries
// attacker-controlled text. The one exception to "only numeric `key`s"
// is the entity link: its href is a fixed route prefix plus a UUID that
// must match UUID_RE, and its text is the server-chosen label — the
// model's answer only ever contributes the handle used to look them up.
// Exported for the chat's comparison table (CompareStoresTable), whose
// store cells link the same way.
export const ENTITY_ROUTES: Record<IaChatEntityKind, string> = {
  store: "/tiendas/",
  report: "/reportes/",
  finding: "/observaciones/",
};
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type EntityMap = ReadonlyMap<string, IaChatEntity>;

export function MarkdownMessage({
  source,
  entities,
}: {
  source: string;
  entities?: readonly IaChatEntity[];
}): ReactNode {
  const byHandle = useMemo<EntityMap>(
    () => new Map((entities ?? []).map((e) => [e.handle, e])),
    [entities]
  );
  const blocks = useMemo(() => parseMarkdown(source, new Set(byHandle.keys())), [source, byHandle]);
  return <>{blocks.map((b, i) => renderBlock(b, i, byHandle))}</>;
}

function renderBlock(block: MdBlock, key: number, ents: EntityMap): ReactNode {
  switch (block.type) {
    case "list": {
      const Tag = block.ordered ? "ol" : "ul";
      const startProp = block.ordered && block.start !== undefined ? { start: block.start } : {};
      return (
        <Tag key={key} {...startProp}>
          {block.items.map((item, i) => (
            <li key={i}>{item.map((n, j) => renderInline(n, j, ents))}</li>
          ))}
        </Tag>
      );
    }
    case "paragraph":
      return <p key={key}>{block.children.map((n, i) => renderInline(n, i, ents))}</p>;
    default: {
      // Exhaustiveness guard: if MdBlock ever grows a new variant (e.g. a
      // future image node), this fails to compile instead of silently
      // rendering nothing — the allowlist stays enforced by the type
      // system, not just by convention.
      const _exhaustive: never = block;
      return _exhaustive;
    }
  }
}

function renderInline(node: MdInline, key: number, ents: EntityMap): ReactNode {
  switch (node.type) {
    case "text":
      return node.value;
    case "strong":
      return <strong key={key}>{node.children.map((n, i) => renderInline(n, i, ents))}</strong>;
    case "em":
      return <em key={key}>{node.children.map((n, i) => renderInline(n, i, ents))}</em>;
    case "code":
      return <code key={key}>{node.value}</code>;
    case "break":
      return <br key={key} />;
    case "entity": {
      const ent = ents.get(node.handle);
      if (!ent) return `[[${node.handle}]]`;
      if (!UUID_RE.test(ent.id)) return ent.label;
      // Plain <a> in a new tab, not <Link>: the conversation lives only in
      // this screen's state, so navigating in place would lose it (same
      // posture as DetalleTienda's «Ir al reporte»).
      return (
        <a
          key={key}
          className="ia-entity-link"
          href={`${ENTITY_ROUTES[ent.kind]}${ent.id}`}
          target="_blank"
          rel="noopener"
          aria-label={`${ent.label} (abre en pestaña nueva)`}
        >
          {ent.label}
        </a>
      );
    }
    default: {
      const _exhaustive: never = node;
      return _exhaustive;
    }
  }
}
